import {
  LitElement,
  html,
  css,
  unsafeCSS,
  nothing,
  type TemplateResult,
} from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import { debounce } from "debounce";

import "@wh-web-front-end/wh-component-library/dist/elements/wh-icon";
import type { PrivacyCard, PrivacyContent } from "../types/content";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-privacy-header": WebKioskPrivacyHeaderElement;
  }
}

@localized()
@customElement("web-kiosk-privacy-header")
export class WebKioskPrivacyHeaderElement extends LitElement {
  @property() content: PrivacyContent | undefined = undefined;

  @property({ type: Number }) debounceTime = 10;

  @query("#header") header: HTMLElement | undefined;

  @query("#navigation") navigation: HTMLElement | undefined;

  connectedCallback(): void {
    super.connectedCallback();

    document.addEventListener("scroll", this.debounceToggleFixedHeader());
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    document.removeEventListener("scroll", this.debounceToggleFixedHeader());
  }

  debounceToggleFixedHeader(): () => void {
    return debounce(() => {
      this.toggleFixedHeader(this);
    }, this.debounceTime);
  }

  toggleFixedHeader(context: WebKioskPrivacyHeaderElement): void {
    if (!context.header) {
      return;
    }

    const fixed = window.scrollY >= context.header.clientHeight;
    this.navigation?.classList.toggle("fixed", fixed);
  }

  renderCards(
    cards: PrivacyCard[] | undefined
  ): TemplateResult[] | typeof nothing {
    if (!cards) {
      return nothing;
    }

    return cards.map(
      (card: PrivacyCard) => html`<div>
        <img src=${card.img} alt=${card.title} />
        <h2>${card.title}</h2>
        <p>${card.description}</p>
      </div>`
    );
  }

  render(): TemplateResult {
    return html`<section id="header">
      <div id="navigation">
        <a
          href="."
          @click=${(event: MouseEvent) => {
            event.preventDefault();
            window.history.back();
          }}
        >
          <wh-icon name="chevron-down"></wh-icon>
          ${msg("Back")}
        </a>
        <h1>${this.content?.headerHeading}</h1>
      </div>
      <div id="header-content">
        <div id="content">
          <section id="texts">
            <h1>${this.content?.headerHeading}</h1>
            <p>${this.content?.headerDescription}</p>
          </section>
          <section id="cards">
            ${this.renderCards(this.content?.headerCards)}
          </section>
        </div>
      </div>
    </section>`;
  }

  static styles = css`
    :host {
      --web-kiosk-bg: #c1e8ff;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-text-color: #323264;
    }

    #header {
      /* stylelint-disable-next-line declaration-colon-space-after */
      background: ${unsafeCSS(`
        url("assets/privacy/bg/swirl.svg") right 40px no-repeat,
        var(--web-kiosk-bg, hotpink);
      `)};
    }

    #header-content {
      padding: 52px 58px;
    }

    #navigation {
      display: flex;
      padding: 52px 58px 0;
      color: var(--web-kiosk-link-color, hotpink);
      font-size: 32px;
    }

    h1 {
      margin: 0 0 8px;
      color: var(--web-kiosk-text-color, hotpink);
      font-size: 64px;
      font-weight: 700;
      line-height: 72px;
    }

    #navigation h1 {
      display: none;
    }

    #navigation.fixed {
      position: fixed;
      width: 100%;
      padding: 36px 59px;
      /* stylelint-disable-next-line declaration-colon-space-after */
      background: ${unsafeCSS(`
        url("assets/privacy/bg/swirl.svg") right 100px top 24px no-repeat,
        var(--web-kiosk-bg, hotpink);
      `)};
      transition: all 0.2s ease;
    }

    #navigation.fixed h1 {
      display: block;
      margin-left: 64px;
    }

    #navigation wh-icon {
      font-size: 50px;
      transform: rotate(90deg);
    }

    #navigation a {
      width: 20%;
      display: flex;
      align-items: center;
      color: var(--web-kiosk-link-color, hotpink);
      text-decoration: none;
      font-weight: bold;
    }

    #content {
      display: flex;
      justify-content: space-between;
    }

    #texts {
      width: 42%;
      padding-top: 36px;
    }

    #texts p {
      font-size: 26px;
      font-weight: 400;
      line-height: 40px;
    }

    #cards {
      display: flex;
      gap: 42px;
      width: 50%;
      padding-top: 36px;
      justify-content: space-between;
    }

    #cards div {
      text-align: center;
    }

    #cards h2 {
      margin: 16px 0 0;
      color: var(--web-kiosk-text-color, hotpink);
      font-size: 26px;
      font-weight: 700;
      line-height: 32px;
    }

    #cards p {
      margin: 8px 0;
      font-size: 22px;
      font-weight: 400;
      line-height: 32px;
    }
  `;
}
